import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useUnreadNotificationCount } from '@/hooks/useNotifications';

interface Props {
  /** Surface-specific notifications route, e.g. "/resident/notifications" */
  to: string;
}

/**
 * Unified notification bell for all layout headers.
 *
 * Replaces 4 divergent implementations (Resident dot, Employee dot,
 * Enterprise numeric Badge, OrgAdmin numeric Badge) with one consistent
 * component — Audit A Pattern B.
 *
 * Shows a 2px accent dot when unread count > 0; the count itself is
 * available as aria text for screen readers.
 */
export function NotificationBell({ to }: Props) {
  const unreadCount = useUnreadNotificationCount();

  return (
    <Button variant="ghost" size="icon" asChild className="relative shrink-0">
      <Link
        to={to}
        aria-label={unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications'}
      >
        <Bell className="size-5 text-[var(--text-secondary)]" />
        {unreadCount > 0 && (
          <span
            className="absolute top-1.5 right-1.5 size-2 rounded-full"
            style={{ background: 'var(--text-accent)' }}
            aria-hidden
          />
        )}
      </Link>
    </Button>
  );
}
